import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Database, BarChart3, Layers, ArrowRight, Globe, Clock, TrendingUp, Activity, Lock } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import TrackerMap from '@/components/tracker/TrackerMap';
const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_ICONS = { einvoicing: FileText, saft: Database, vat_returns: BarChart3, vat_structure: Layers };
const STATUS_META = { active: { badge: 'Live', color: '#16a34a' }, coming_soon: { badge: 'Coming Soon', color: '#94a3b8' } };
const SHORT_NAMES = { einvoicing: 'e-Invoicing', saft: 'SAF-T', vat_returns: 'VAT returns', vat_structure: 'VAT structure' };
const FALLBACK_CATEGORIES = [
  { id: 'einvoicing', name: 'e-Invoicing & Digital VAT', description: '', status: 'active' },
  { id: 'saft', name: 'SAF-T & Digital Audit', description: '', status: 'active' },
  { id: 'vat_returns', name: 'VAT Returns & Reporting', description: '', status: 'active' },
  { id: 'vat_structure', name: 'VAT Structure', description: '', status: 'active' },
];
export default function TrackerHubPage() {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [cache, setCache] = useState({});
  const [params, setParams] = useSearchParams();
  const cats = categories.length ? categories : FALLBACK_CATEGORIES;
  const requested = params.get('topic');
  const topic = cats.some((c) => c.id === requested && c.status === 'active') ? requested : 'einvoicing';
  const activeCat = cats.find((c) => c.id === topic);
  const items = cache[topic];
  useEffect(() => {
    fetch(`${API}/api/tracker/categories`).then((r) => r.json()).then((d) => setCategories(d.categories || [])).catch(() => {});
    fetch(`${API}/api/tracker/stats`).then((r) => r.json()).then(setStats).catch(() => {});
  }, []);
  useEffect(() => {
    if (cache[topic]) return;
    fetch(`${API}/api/tracker/content?category=${topic}`)
      .then((r) => r.json())
      .then((d) => setCache((prev) => ({ ...prev, [topic]: d.items || [] })))
      .catch(() => setCache((prev) => ({ ...prev, [topic]: [] })));
  }, [topic]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickTopic = (id) => {
    const next = new URLSearchParams(params);
    next.set('topic', id);
    setParams(next, { replace: true });
  };
  return (
    <PageLayout>
      <div className="min-h-screen" data-testid="tracker-hub-page">
        {/* Compact header and the map straight away */}
        <section className="pt-8 pb-10 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-taxera-charcoal leading-tight" style={{ textWrap: 'balance' }}>
                  Track global tax change. <span className="taxera-gradient-text">Understand what it means.</span>
                </h1>
                <p className="mt-2 text-sm text-slate-500 max-w-2xl">
                  The simplest way for tax, finance and compliance teams to follow regulatory change in indirect tax.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Globe className="w-4 h-4 text-taxera-teal" /> {stats ? `${stats.total_countries_tracked} countries tracked` : 'Countries tracked'}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-taxera-teal" /> Updated weekly</span>
                <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-taxera-teal" /> Free, no paywall</span>
              </div>
            </div>
            {/* Topic switch */}
            <div className="flex gap-2 overflow-x-auto pb-1 mb-4" role="tablist" data-testid="tracker-topic-tabs">
              {cats.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || FileText;
                const isActive = cat.status === 'active';
                const selected = cat.id === topic;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    disabled={!isActive}
                    onClick={() => isActive && pickTopic(cat.id)}
                    className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                      selected
                        ? 'bg-taxera-blue text-white shadow-sm'
                        : isActive
                          ? 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
                          : 'bg-slate-50 border border-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid={`tracker-tab-${cat.id}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                    {SHORT_NAMES[cat.id] || cat.name}
                    {!isActive && <Lock className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
            {activeCat && (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-taxera-charcoal">{activeCat.name}.</span> {activeCat.description}
                </p>
                <Link to={`/tracker/${topic}`} className="text-sm font-bold text-taxera-blue inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" data-testid="tracker-see-list">
                  See all countries as a list <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
            {items ? (
              <TrackerMap items={items} categoryId={topic} />
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 text-center text-slate-400 py-24" style={{ aspectRatio: '2.2/1' }}>
                Loading the map...
              </div>
            )}
          </div>
        </section>
        {/* Topic cards, now below the map */}
        <section className="py-14 lg:py-16 bg-slate-50 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-heading text-xl lg:text-2xl font-bold text-taxera-charcoal mb-6">Explore by topic</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {categories.map((cat, i) => {
                const Icon = CATEGORY_ICONS[cat.id] || FileText;
                const meta = STATUS_META[cat.status] || STATUS_META.coming_soon;
                const isActive = cat.status === 'active';
                return (
                  <motion.div key={cat.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                    {isActive ? (
                      <Link
                        to={`/tracker/${cat.id}`}
                        className="group block h-full bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 hover:shadow-xl hover:border-taxera-teal/30 transition-all duration-300"
                        data-testid={`tracker-card-${cat.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-xl bg-taxera-blue/5 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-taxera-blue" strokeWidth={1.5} />
                          </div>
                          <span className="text-2xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: meta.color }}>{meta.badge}</span>
                        </div>
                        <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-2 group-hover:text-taxera-blue transition-colors">{cat.name}</h3>
                        <p className="text-sm text-slate-500 mb-4 leading-relaxed">{cat.description}</p>
                        <div className="flex items-center justify-between">
                          {cat.country_count > 0 && <span className="text-xs text-slate-400">{cat.country_count} countries</span>}
                          <span className="text-xs font-bold text-taxera-teal flex items-center gap-1 group-hover:gap-2 transition-all">
                            Explore <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </Link>
                    ) : (
                      <div className="h-full bg-slate-50 rounded-2xl border border-slate-200/60 p-6 lg:p-8 opacity-70" data-testid={`tracker-card-${cat.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-xl bg-slate-200/50 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                          </div>
                          <span className="text-2xs font-bold px-2.5 py-1 rounded-full text-white flex items-center gap-1" style={{ backgroundColor: meta.color }}>
                            <Lock className="w-3 h-3" /> {meta.badge}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg font-bold text-slate-500 mb-2">{cat.name}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{cat.description}</p>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
        {/* Recent updates */}
        {stats?.recent_updates?.length > 0 && (
          <section className="py-14 lg:py-16">
            <div className="max-w-6xl mx-auto px-6">
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 lg:p-8" data-testid="tracker-recent-updates">
                <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-taxera-teal" /> Recent Updates
                </h3>
                <div className="space-y-3">
                  {stats.recent_updates.map((u, i) => (
                    <Link
                      key={`update-${u.country_code}-${i}`}
                      to={`/tracker/einvoicing/${u.country_code?.toLowerCase()}`}
                      className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-white transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={`https://flagcdn.com/24x18/${u.country_code?.toLowerCase()}.png`}
                          alt={u.country_name}
                          className="w-6 h-4.5 rounded-sm object-cover border border-slate-200"
                        />
                        <span className="text-sm font-semibold text-taxera-charcoal group-hover:text-taxera-blue transition-colors">{u.country_name}</span>
                      </div>
                      <span className="text-xs text-slate-400">{u.last_updated}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
        {/* Value proposition */}
        <section className="py-16 lg:py-20 bg-slate-900">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-4">Not just data. Practical understanding.</h2>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto mb-10">
              Every country entry goes beyond dates and mandates. We explain what the regulation means in practice: the business process implications, the system dependencies, and the things companies commonly get wrong.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: 'Baseline', desc: 'Mandate, dates, scope, official sources. Clear and current.', accent: '#16a34a' },
                { title: 'Business Process', desc: 'What it means for AR/AP, ERP integration, and data flows.', accent: '#0066CC' },
                { title: 'Practical Insight', desc: 'What goes wrong, dependencies between mandates, and what to plan for.', accent: '#f59e0b' },
              ].map((l) => (
                <div key={`vp-${l.title}`} className="text-left p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-2 h-2 rounded-full mb-3" style={{ backgroundColor: l.accent }} />
                  <h4 className="font-heading text-sm font-bold text-white mb-1">{l.title}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{l.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
